/**
 * A decimal string read as an integer scaled by a power of ten, so everything
 * downstream of it is integer arithmetic.
 *
 * Money and the factors applied to money are `bigint` minor units here
 * (CLAUDE.md 2.1/2.2): `Number('0.29') * 3000` is 869.9999999999999, and
 * flooring it files a figure a minor unit below the one that was agreed. A
 * decimal string is an exact integer once its point is shifted, and that is the
 * only conversion this does.
 *
 * Whether fractional digits were DROPPED is reported rather than decided: a rate
 * an operator typed should be refused for being too precise to file, while a
 * campaign term that arrives with more digits than the scale holds is better
 * truncated than thrown away. Both callers read the same field and answer it
 * differently.
 */

/** Digits before the point are bounded so an absurd paste cannot become a bigint of any size. */
const DECIMAL = /^(\d{1,14})(?:\.(\d+))?$/;

export interface ScaledDecimal {
  /** The value times 10^scale, exactly. */
  scaled: bigint;
  /** True when the text carried more fractional digits than `scale` could hold. */
  excessDigits: boolean;
}

/**
 * `'712.5'` at scale 6 → `712500000n`. `null` for blank, negative, or anything
 * that is not a plain decimal; zero parses as zero, which is a value and not an
 * error. Fractional digits past `scale` are truncated toward zero (§2.5) and
 * flagged.
 */
export function parseScaledDecimal(
  text: string | null | undefined,
  scale: number,
): ScaledDecimal | null {
  const m = DECIMAL.exec((text ?? '').trim());
  if (!m) return null;
  const fraction = m[2] ?? '';
  const kept = fraction.slice(0, scale).padEnd(scale, '0');
  return {
    scaled: BigInt(m[1]!) * 10n ** BigInt(scale) + BigInt(kept === '' ? '0' : kept),
    excessDigits: fraction.length > scale,
  };
}
